import { useEffect, useRef, useState } from 'react';
import { FaAward, FaBuilding, FaUsers, FaTrophy, FaHeart, FaShieldAlt, FaStar, FaQuoteLeft } from 'react-icons/fa';
import { GiSparkles, GiProgression, GiTeamIdea, GiDiamondRing } from 'react-icons/gi';
import { MdOutlineTrendingUp } from 'react-icons/md';
import { RiDoubleQuotesL } from 'react-icons/ri';

const About = () => {
  const [counters, setCounters] = useState({
    awards: 0,
    team: 0,
    projects: 0,
    clients: 0
  });
  
  const statsRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const step = 20;
      const targets = { awards: 15, team: 6, projects: 500, clients: 1000 };
      const increments = {
        awards: targets.awards / (duration / step),
        team: targets.team / (duration / step),
        projects: targets.projects / (duration / step),
        clients: targets.clients / (duration / step)
      };
      
      let current = { awards: 0, team: 0, projects: 0, clients: 0 };
      const timer = setInterval(() => {
        let allComplete = true;
        
        Object.keys(targets).forEach(key => {
          if (current[key] < targets[key]) {
            allComplete = false;
            current[key] = Math.min(current[key] + increments[key], targets[key]);
          }
        });
        
        setCounters({ ...current });
        
        if (allComplete) clearInterval(timer);
      }, step);
      
      return () => clearInterval(timer);
    }
  }, [isVisible]);

  const milestones = [
    { year: '2009', title: 'Company Founded', description: 'Started our journey with a vision to deliver premium real estate solutions', icon: GiSparkles },
    { year: '2014', title: 'Expansion Phase', description: 'Expanded operations to multiple cities across North India', icon: FaBuilding },
    { year: '2019', title: 'Diversification', description: 'Launched diverse portfolio including residential, commercial, and mixed-use properties', icon: MdOutlineTrendingUp },
    { year: '2024', title: 'Industry Leadership', description: 'Recognized as trusted real estate partner with 15+ years of excellence', icon: FaTrophy },
  ];

  const coreValues = [
    { icon: FaHeart, title: '15+ Years Experience', description: 'Trusted industry leadership since 2009' },
    { icon: FaShieldAlt, title: 'Transparent Dealings', description: 'Honest transactions with legal assistance' },
    { icon: FaStar, title: 'Wide Portfolio', description: 'Residential & commercial properties' },
    { icon: FaUsers, title: 'Customer First', description: 'End-to-end assistance for clients' },
  ];

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes aboutFadeUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes aboutSlideLeft {
        from { opacity: 0; transform: translateX(-40px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes aboutSlideRight {
        from { opacity: 0; transform: translateX(40px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes aboutShimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes aboutFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      @keyframes aboutPulse {
        0%, 100% { opacity: 0.15; transform: scale(1); }
        50% { opacity: 0.3; transform: scale(1.05); }
      }
      @keyframes aboutCountUp {
        from { opacity: 0; transform: scale(0.8); }
        to { opacity: 1; transform: scale(1); }
      }
      .animate-about-fade-up { animation: aboutFadeUp 0.8s ease-out forwards; }
      .animate-about-slide-left { animation: aboutSlideLeft 0.8s ease-out forwards; }
      .animate-about-slide-right { animation: aboutSlideRight 0.8s ease-out forwards; }
      .animate-about-float { animation: aboutFloat 3s ease-in-out infinite; }
      .animate-about-pulse { animation: aboutPulse 4s ease-in-out infinite; }
      .delay-100 { animation-delay: 0.1s; }
      .delay-200 { animation-delay: 0.2s; }
      .delay-300 { animation-delay: 0.3s; }
      .delay-400 { animation-delay: 0.4s; }
      .font-serif { font-family: 'Cormorant Garamond', serif; }
      .font-montserrat { font-family: 'Montserrat', sans-serif; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <section id="about" className="relative py-16 sm:py-20 md:py-28 lg:py-32 overflow-hidden bg-navy">
      {/* Ambient Background - Matching Hero Section */}
      <div className="absolute inset-0">
        {/* Dark base gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy"></div>
        
        {/* Golden orb glow - top left */}
        <div className="absolute top-0 left-0 w-[20rem] h-[20rem] sm:w-[30rem] sm:h-[30rem] md:w-[40rem] md:h-[40rem] bg-gold/5 rounded-full blur-3xl animate-about-pulse"></div>
        
        {/* Golden orb glow - bottom right */}
        <div className="absolute bottom-0 right-0 w-[18rem] h-[18rem] sm:w-[25rem] sm:h-[25rem] md:w-[35rem] md:h-[35rem] bg-amber-500/5 rounded-full blur-3xl animate-about-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating particles */}
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-gold/20 rounded-full animate-about-float"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 10 + 8}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-8 lg:px-12 z-10">
        {/* Section Header - Matching Hero Typography */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20 animate-about-fade-up">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
            <span className="text-gold text-[9px] sm:text-[10px] md:text-[11px] font-semibold tracking-[0.2em] sm:tracking-[0.24em] md:tracking-[0.28em] uppercase font-montserrat">Our Legacy</span>
            <div className="w-6 sm:w-8 h-px bg-gold"></div>
          </div>
          
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-3 sm:mb-4">
            Crafting Dreams Into
            <span className="block text-gold font-normal italic mt-1 sm:mt-2">Architectural Masterpieces</span>
          </h2>
          
          <div className="w-12 sm:w-16 h-px bg-gold/50 mx-auto mt-5 sm:mt-6 mb-5 sm:mb-6"></div>
          
          <p className="text-gray-300 max-w-3xl mx-auto text-xs sm:text-sm md:text-base leading-relaxed font-light px-4">
            Purvanchal Global Ventures Pvt. Ltd. is a trusted and fast-growing real estate company 
            dedicated to delivering premium property solutions across North India.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
          {/* Left Column - Content */}
          <div className="space-y-6 sm:space-y-8 animate-about-slide-left">
            {/* Quote Card */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 sm:-top-6 sm:-left-6 w-16 h-16 sm:w-20 sm:h-20 bg-gold/10 rounded-full blur-2xl"></div>
              <div className="relative bg-black/40 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/10">
                <RiDoubleQuotesL size={32} className="text-gold/30 mb-3 sm:mb-4" />
                <p className="text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed mb-4 sm:mb-6 font-light">
                  Purvanchal Global Ventures Pvt. Ltd. is a trusted and fast-growing real estate company 
                  dedicated to delivering premium property solutions across North India. With 15+ years of experience, 
                  we have built a strong reputation for transparency, reliability, and customer satisfaction.
                </p>
                <p className="text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed mb-4 sm:mb-6 font-light">
                  We specialize in buying, selling, and renting a wide range of properties including 
                  residential plots, villas, independent floors, flats, and commercial spaces. Our presence spans 
                  across key cities like Prayagraj, Lucknow, Noida, Gurgaon, Chandigarh, and Delhi.
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-navy font-bold text-sm sm:text-base">PG</span>
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm sm:text-base font-serif">Purvanchal Global Ventures</div>
                    <div className="text-gold text-[10px] sm:text-xs uppercase tracking-wider">15+ Years of Excellence</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Values */}
            <div>
              <h3 className="text-xl sm:text-2xl font-light text-white mb-4 sm:mb-6 flex items-center gap-2 font-serif">
                <FaHeart size={18} className="text-gold sm:text-2xl" />
                Why Choose Us?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {coreValues.map((value, idx) => (
                  <div 
                    key={idx}
                    className="group bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/10 hover:border-gold/30 transition-all duration-300 hover:transform hover:-translate-y-1"
                  >
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gold/15 flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300">
                      <value.icon size={16} className="text-gold sm:text-xl" />
                    </div>
                    <h4 className="font-semibold text-white text-sm sm:text-base mb-1 sm:mb-2">{value.title}</h4>
                    <p className="text-gray-400 text-[10px] sm:text-xs leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <button className="group relative bg-gold text-navy px-6 sm:px-8 py-2.5 sm:py-3 rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 w-full sm:w-auto overflow-hidden text-sm sm:text-base font-semibold tracking-wide">
              <span className="relative z-10 flex items-center gap-2">
                Discover Our Story
                <GiProgression className="group-hover:translate-x-1 transition-transform" size={16} />
              </span>
            </button>
          </div>

          {/* Right Column - Image Gallery */}
          <div className="relative animate-about-slide-right">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-3 sm:space-y-4">
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl group">
                  <img 
                    src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&w=600&h=450&fit=crop" 
                    alt="Luxury Building"
                    className="w-full h-40 sm:h-52 md:h-60 lg:h-72 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl group">
                  <img 
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&w=600&h=400&fit=crop" 
                    alt="Interior Design"
                    className="w-full h-32 sm:h-44 md:h-48 lg:h-56 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6 md:mt-8">
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl group">
                  <img 
                    src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&w=600&h=450&fit=crop" 
                    alt="Modern Architecture"
                    className="w-full h-36 sm:h-48 md:h-52 lg:h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="relative overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl group">
                  <img 
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&w=600&h=450&fit=crop" 
                    alt="Luxury Apartment"
                    className="w-full h-32 sm:h-40 md:h-44 lg:h-56 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-3 sm:-bottom-5 sm:-right-4 md:-bottom-6 md:-right-6 bg-gradient-to-r from-gold to-amber-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl animate-about-float">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <FaAward size={16} className="text-white sm:text-xl md:text-2xl" />
                </div>
                <div>
                  <div className="text-white font-bold text-base sm:text-lg md:text-xl">25+</div>
                  <div className="text-white/80 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-wider">Industry Awards</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section with Counters - Matching Hero Panel Style */}
        <div ref={statsRef} className="mt-12 sm:mt-16 md:mt-20 lg:mt-24">
          <div className="bg-black/40 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 border border-white/10 shadow-2xl">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {[
                { label: 'Years Experience', value: counters.awards, icon: FaAward, suffix: '+' },
                { label: 'Cities Presence', value: counters.team, icon: FaUsers, suffix: '+' },
                { label: 'Properties Delivered', value: counters.projects, icon: FaBuilding, suffix: '+' },
                { label: 'Happy Clients', value: counters.clients, icon: FaHeart, suffix: '+' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center group cursor-pointer">
                  <div className="flex justify-center mb-3 sm:mb-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gold/15 flex items-center justify-center group-hover:bg-gold/25 transition-all duration-300 group-hover:scale-110 border border-gold/30">
                      <stat.icon size={20} className="text-gold sm:text-2xl md:text-3xl" />
                    </div>
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-1 sm:mb-2 font-serif">
                    {Math.floor(stat.value)}{stat.suffix}
                  </div>
                  <div className="text-gold/70 text-[9px] sm:text-[10px] md:text-xs uppercase tracking-wider font-montserrat">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Journey Timeline */}
        <div className="mt-12 sm:mt-16 md:mt-20 lg:mt-24">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-3 sm:mb-4 font-serif">Our Glorious Journey</h3>
            <div className="w-12 sm:w-16 h-px bg-gold/50 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-gray-400 max-w-2xl mx-auto text-xs sm:text-sm font-light">A decade of excellence, innovation, and trust</p>
          </div>
          
          <div className="relative">
            {/* Timeline line - hidden on mobile */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-gradient-to-b from-gold/30 via-gold to-gold/30 rounded-full hidden md:block"></div>
            
            <div className="space-y-6 sm:space-y-8 md:space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Timeline dot - hidden on mobile */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-3 h-3 md:w-4 md:h-4 bg-gold rounded-full border-2 border-navy shadow-lg z-10 hidden md:block"></div>
                  
                  <div className={`md:w-5/12 ${index % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10 md:ml-auto md:text-left'} w-full mb-4 md:mb-0`}>
                    <div className="group bg-black/30 backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-white/10 hover:border-gold/30 transition-all duration-300 hover:transform hover:-translate-y-1">
                      <div className="inline-flex items-center gap-2 bg-gold/20 rounded-full px-2.5 sm:px-3 py-1 mb-3 sm:mb-4">
                        <milestone.icon size={10} className="text-gold sm:text-xs md:text-sm" />
                        <span className="text-gold text-[9px] sm:text-[10px] md:text-xs font-semibold tracking-wider">{milestone.year}</span>
                      </div>
                      <h4 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-1 sm:mb-2 group-hover:text-gold transition-colors font-serif">
                        {milestone.title}
                      </h4>
                      <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Decorative Line */}
        <div className="mt-12 sm:mt-16 md:mt-20 pt-6 sm:pt-8 border-t border-white/10">
          <div className="flex justify-center">
            <div className="w-16 sm:w-20 h-px bg-gold/30"></div>
          </div>
          <p className="text-center text-gray-500 text-[9px] sm:text-[10px] mt-4 sm:mt-5 tracking-wider uppercase">
            Building Dreams, Creating Landmarks
          </p>
        </div>
      </div>
    </section>
  );
};

export default About;